import { Platform } from 'react-native';
import type { ItineraryItem } from '@/services/itinerary';
import { applePlatformFlags } from '@/config/applePlatformFlags';
import { networkMonitor } from '@/services/networkMonitor';
import {
  syncAppleTripSurfaceSnapshot,
  syncAppleTripSurfaceSnapshotFromCache,
  pickAppleTripSurfaceTrip,
} from '@/services/appleTripSurfaceSync';
import { refreshTripSurfaceLiveActivityFromCache } from '@/services/appleWidgetV1';

/** Push / collaboration events that should refresh widget + Live Activity snapshots. */
export const TRIP_SURFACE_REFRESH_NOTIFICATION_TYPES = new Set([
  'trip_surface_refresh',
  'collaboration',
  'comment_added',
  'maybe_idea_added',
  'flight_delay',
  'flight_gate_change',
  'flight_checkin',
  'weather_alert',
  'weather_forecast',
]);

export type RefreshAppleTripSurfaceSnapshotOpts = {
  userId: string;
  tripId?: string;
  /** Attempt Firestore fetch before falling back to offline cache. Default true. */
  preferNetwork?: boolean;
  /** Force server reads when online. Default true for network refresh. */
  fromServer?: boolean;
};

export const shouldRefreshTripSurfaceForNotification = (
  data: Record<string, unknown> | undefined,
): boolean => {
  const type = data?.type;
  return typeof type === 'string' && TRIP_SURFACE_REFRESH_NOTIFICATION_TYPES.has(type);
};

/**
 * Fetch live trips + surface-trip itinerary, persist to offline cache, and write App Group snapshot.
 */
export const syncAppleTripSurfaceSnapshotFromNetwork = async (
  userId: string,
  options?: { tripId?: string; fromServer?: boolean },
): Promise<void> => {
  const { refreshTripListFromServer } = await import('./trips');
  const { fetchTripItineraryItems } = await import('./itinerary');
  const { cacheTripData, getCachedTripData, getCachedTrips } = await import('./offlineTrips');

  let trips = await refreshTripListFromServer(userId).catch(async () => getCachedTrips(userId));
  if (!trips.length) {
    trips = await getCachedTrips(userId);
  }

  const surfaceTrip =
    (options?.tripId ? trips.find((trip) => trip.id === options.tripId) : null) ??
    pickAppleTripSurfaceTrip({ trips, userId });

  if (!surfaceTrip?.id) {
    await syncAppleTripSurfaceSnapshotFromCache(userId);
    return;
  }

  let itineraryItems: ItineraryItem[] = [];
  if (networkMonitor.isOnline()) {
    itineraryItems = await fetchTripItineraryItems(surfaceTrip.id, {
      fromServer: options?.fromServer ?? true,
    });
  }

  if (itineraryItems.length === 0) {
    try {
      const cached = await getCachedTripData(surfaceTrip.id);
      const { itineraryItemsFromOfflineCache } = await import('./offlineTrips');
      itineraryItems = itineraryItemsFromOfflineCache(cached?.itinerary ?? [], surfaceTrip.id);
    } catch {
      itineraryItems = [];
    }
  } else {
    try {
      const cached = await getCachedTripData(surfaceTrip.id);
      await cacheTripData(
        surfaceTrip,
        {
          expenses: cached?.expenses,
          bookings: cached?.bookings,
          itinerary: itineraryItems,
        },
        { force: true },
      );
    } catch {
      // Best-effort offline persistence for the next background wake.
    }
  }

  await syncAppleTripSurfaceSnapshot({
    trips,
    userId,
    selectedTrip: surfaceTrip,
    selectedItineraryItems: itineraryItems,
  });
};

/** Unified entry for foreground, background, and push-triggered widget refresh. */
export const refreshAppleTripSurfaceSnapshot = async (
  opts: RefreshAppleTripSurfaceSnapshotOpts,
): Promise<void> => {
  if (Platform.OS !== 'ios') return;
  if (!applePlatformFlags.phase5Enabled || !applePlatformFlags.widgetNextTripEnabled) return;

  const preferNetwork = opts.preferNetwork ?? true;
  if (preferNetwork && networkMonitor.isOnline()) {
    try {
      await syncAppleTripSurfaceSnapshotFromNetwork(opts.userId, {
        tripId: opts.tripId,
        fromServer: opts.fromServer ?? true,
      });
      await refreshTripSurfaceLiveActivityFromCache();
      return;
    } catch {
      // Fall back to offline cache below.
    }
  }

  await syncAppleTripSurfaceSnapshotFromCache(opts.userId);
  await refreshTripSurfaceLiveActivityFromCache();
};
